import { calc3dUiConstants } from '../actions/constants'

const defaultCategoryState = {
    confirmedBlocks: {},
    highlightedBlocks: {},
    expandedCategories: {},
    draftSelectedModels: {},
    draftColors: {},
    openDropdownCatId: null,
}

const defaultSceneDock = {
    isDockVisible: true,
    isCategoriesOpen: false,
    activeTool: 'mode',
}

const defaultRightPanel = {
    openKey: 'scenes',
}

const defaultActionHint = {
    id: null,
    label: '',
    userName: '',
    role: '',
    source: '',
    ts: null,
}

const initialState = {
    activeTab: 'calculator',
    isHeaderVisible: true,
    isPanelOpen: false,
    cartDialogOpen: false,

    renderModalOpen: false,
    renderModalCurrentId: null,

    categoryState: defaultCategoryState,

    sceneDock: defaultSceneDock,
    rightPanel: defaultRightPanel,
    actionHint: defaultActionHint,

    activePanel: 'scenes',
    activeTool: 'mode',

    updatedAt: null,
}

const safeObject = (value) => (
    value && typeof value === 'object' && !Array.isArray(value) ? value : {}
)

const normalizeCategoryState = (value = {}) => ({
    confirmedBlocks: safeObject(value.confirmedBlocks),
    highlightedBlocks: safeObject(value.highlightedBlocks),
    expandedCategories: safeObject(value.expandedCategories),
    draftSelectedModels: safeObject(value.draftSelectedModels),
    draftColors: safeObject(value.draftColors),
    openDropdownCatId: value.openDropdownCatId || null,
})

const mergeCategoryState = (current = {}, patch = {}) => ({
    ...normalizeCategoryState(current),
    ...patch,
    confirmedBlocks: Object.prototype.hasOwnProperty.call(patch, 'confirmedBlocks')
        ? safeObject(patch.confirmedBlocks)
        : safeObject(current.confirmedBlocks),
    highlightedBlocks: Object.prototype.hasOwnProperty.call(patch, 'highlightedBlocks')
        ? safeObject(patch.highlightedBlocks)
        : safeObject(current.highlightedBlocks),
    expandedCategories: Object.prototype.hasOwnProperty.call(patch, 'expandedCategories')
        ? safeObject(patch.expandedCategories)
        : safeObject(current.expandedCategories),
    draftSelectedModels: Object.prototype.hasOwnProperty.call(patch, 'draftSelectedModels')
        ? safeObject(patch.draftSelectedModels)
        : safeObject(current.draftSelectedModels),
    draftColors: Object.prototype.hasOwnProperty.call(patch, 'draftColors')
        ? safeObject(patch.draftColors)
        : safeObject(current.draftColors),
    openDropdownCatId: Object.prototype.hasOwnProperty.call(patch, 'openDropdownCatId')
        ? (patch.openDropdownCatId || null)
        : (current.openDropdownCatId || null),
})

const normalizeSceneDock = (value = {}) => ({
    isDockVisible: Object.prototype.hasOwnProperty.call(value, 'isDockVisible')
        ? !!value.isDockVisible
        : defaultSceneDock.isDockVisible,
    isCategoriesOpen: Object.prototype.hasOwnProperty.call(value, 'isCategoriesOpen')
        ? !!value.isCategoriesOpen
        : defaultSceneDock.isCategoriesOpen,
    activeTool: Object.prototype.hasOwnProperty.call(value, 'activeTool')
        ? (value.activeTool || null)
        : defaultSceneDock.activeTool,
})

const mergeSceneDock = (current = {}, patch = {}) => ({
    ...normalizeSceneDock(current),
    ...patch,
    isDockVisible: Object.prototype.hasOwnProperty.call(patch, 'isDockVisible')
        ? !!patch.isDockVisible
        : normalizeSceneDock(current).isDockVisible,
    isCategoriesOpen: Object.prototype.hasOwnProperty.call(patch, 'isCategoriesOpen')
        ? !!patch.isCategoriesOpen
        : normalizeSceneDock(current).isCategoriesOpen,
    activeTool: Object.prototype.hasOwnProperty.call(patch, 'activeTool')
        ? (patch.activeTool || null)
        : normalizeSceneDock(current).activeTool,
})

const normalizeRightPanel = (value = {}) => ({
    openKey: Object.prototype.hasOwnProperty.call(value, 'openKey')
        ? (value.openKey || null)
        : defaultRightPanel.openKey,
})

const mergeRightPanel = (current = {}, patch = {}) => ({
    ...normalizeRightPanel(current),
    ...patch,
    openKey: Object.prototype.hasOwnProperty.call(patch, 'openKey')
        ? (patch.openKey || null)
        : normalizeRightPanel(current).openKey,
})

const normalizeActionHint = (value = {}) => ({
    id: value.id || null,
    label: value.label || '',
    userName: value.userName || '',
    role: value.role || '',
    source: value.source || '',
    ts: value.ts || null,
})

const applyFullState = (state, payload = {}) => {
    const nextSceneDock = Object.prototype.hasOwnProperty.call(payload, 'sceneDock')
        ? mergeSceneDock(state.sceneDock, payload.sceneDock || {})
        : mergeSceneDock(state.sceneDock, {
            activeTool: Object.prototype.hasOwnProperty.call(payload, 'activeTool')
                ? payload.activeTool
                : state.sceneDock?.activeTool,
        })

    const nextRightPanel = Object.prototype.hasOwnProperty.call(payload, 'rightPanel')
        ? mergeRightPanel(state.rightPanel, payload.rightPanel || {})
        : mergeRightPanel(state.rightPanel, {
            openKey: Object.prototype.hasOwnProperty.call(payload, 'activePanel')
                ? payload.activePanel
                : state.rightPanel?.openKey,
        })

    const hasActionHint = Object.prototype.hasOwnProperty.call(payload, 'actionHint')

    return {
        ...state,
        activeTab: Object.prototype.hasOwnProperty.call(payload, 'activeTab')
            ? (payload.activeTab || 'calculator')
            : state.activeTab,
        isHeaderVisible: Object.prototype.hasOwnProperty.call(payload, 'isHeaderVisible')
            ? payload.isHeaderVisible !== false
            : state.isHeaderVisible,
        isPanelOpen: Object.prototype.hasOwnProperty.call(payload, 'isPanelOpen')
            ? !!payload.isPanelOpen
            : state.isPanelOpen,
        cartDialogOpen: Object.prototype.hasOwnProperty.call(payload, 'cartDialogOpen')
            ? !!payload.cartDialogOpen
            : state.cartDialogOpen,
        renderModalOpen: Object.prototype.hasOwnProperty.call(payload, 'renderModalOpen')
            ? !!payload.renderModalOpen
            : state.renderModalOpen,
        renderModalCurrentId: Object.prototype.hasOwnProperty.call(payload, 'renderModalCurrentId')
            ? (payload.renderModalCurrentId || null)
            : state.renderModalCurrentId,
        categoryState: Object.prototype.hasOwnProperty.call(payload, 'categoryState')
            ? normalizeCategoryState(payload.categoryState || {})
            : normalizeCategoryState(state.categoryState),
        sceneDock: nextSceneDock,
        rightPanel: nextRightPanel,
        actionHint: hasActionHint
            ? normalizeActionHint(payload.actionHint || {})
            : normalizeActionHint(state.actionHint),
        activePanel: nextRightPanel.openKey,
        activeTool: nextSceneDock.activeTool,
        updatedAt: Date.now(),
    }
}

const calc3dUiReducer = (state = initialState, action) => {
    switch (action.type) {
        case calc3dUiConstants.SET_CALC3D_ACTIVE_TAB:
            return {
                ...state,
                activeTab: action.payload || 'calculator',
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.SET_CALC3D_HEADER_VISIBLE:
            return {
                ...state,
                isHeaderVisible: !!action.payload,
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.SET_CALC3D_PANEL_OPEN:
            return {
                ...state,
                isPanelOpen: !!action.payload,
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.TOGGLE_CALC3D_PANEL_OPEN:
            return {
                ...state,
                isPanelOpen: !state.isPanelOpen,
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.TOGGLE_CALC3D_HEADER_VISIBLE:
            return {
                ...state,
                isHeaderVisible: !state.isHeaderVisible,
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.SET_CALC3D_CART_DIALOG_OPEN:
            return {
                ...state,
                cartDialogOpen: !!action.payload,
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.SET_CALC3D_RENDER_MODAL:
            return {
                ...state,
                renderModalOpen: !!action.payload?.open,
                renderModalCurrentId: action.payload?.currentIdPicture || null,
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.SET_CALC3D_RENDER_MODAL_OPEN:
            return {
                ...state,
                renderModalOpen: !!action.payload,
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.SET_CALC3D_RENDER_MODAL_CURRENT_ID:
            return {
                ...state,
                renderModalCurrentId: action.payload || null,
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.PATCH_CALC3D_CATEGORY_STATE:
            return {
                ...state,
                categoryState: mergeCategoryState(state.categoryState, action.payload || {}),
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.REPLACE_CALC3D_CATEGORY_STATE:
            return {
                ...state,
                categoryState: normalizeCategoryState(action.payload || {}),
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.SET_CALC3D_SCENE_DOCK: {
            const nextSceneDock = mergeSceneDock(state.sceneDock, action.payload || {})

            return {
                ...state,
                sceneDock: nextSceneDock,
                activeTool: nextSceneDock.activeTool,
                updatedAt: Date.now(),
            }
        }

        case calc3dUiConstants.SET_CALC3D_SCENE_DOCK_FIELD: {
            const field = action.payload?.field
            const value = action.payload?.value

            if (!field) return state

            const nextSceneDock = mergeSceneDock(state.sceneDock, {
                [field]: value,
            })

            return {
                ...state,
                sceneDock: nextSceneDock,
                activeTool: nextSceneDock.activeTool,
                updatedAt: Date.now(),
            }
        }

        case calc3dUiConstants.SET_CALC3D_RIGHT_PANEL_OPEN_KEY: {
            const nextRightPanel = mergeRightPanel(state.rightPanel, {
                openKey: action.payload || null,
            })

            return {
                ...state,
                rightPanel: nextRightPanel,
                activePanel: nextRightPanel.openKey,
                updatedAt: Date.now(),
            }
        }

        case calc3dUiConstants.SET_CALC3D_ACTION_HINT:
            return {
                ...state,
                actionHint: normalizeActionHint(action.payload || {}),
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.CLEAR_CALC3D_ACTION_HINT:
            return {
                ...state,
                actionHint: defaultActionHint,
                updatedAt: Date.now(),
            }

        case calc3dUiConstants.PATCH_CALC3D_UI_STATE:
            return applyFullState(state, {
                ...state,
                ...(action.payload || {}),
            })

        case calc3dUiConstants.APPLY_CALC3D_UI_STATE:
            return applyFullState(state, action.payload || {})

        case calc3dUiConstants.RESET_CALC3D_UI_STATE:
            return {
                ...initialState,
                updatedAt: Date.now(),
            }

        default:
            return state
    }
}

export default calc3dUiReducer
